import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { message, Tooltip } from "antd";
import DataParser from "../../../utlits/Dataparser";
import CompanyService from "../../../Services/CompanyService";
import secureLocalStorage from "react-secure-storage";
import SocketService from "../../../Services/SocketService";
import TableSkeleton from "../../Reuseable/TableSkeleton";
import "react-loading-skeleton/dist/skeleton.css";

message.config({ zIndex: 1000000001 });

const MODULE_NAMES = {
  role: "Role", user: "User", leads: "Leads", leadstatus: "Lead Status",
  tasks: "Tasks", jobstatus: "Job Status", departments: "Departments",
  priority: "Priority", attendance: "Attendance", leaveperiod: "Leave Period",
  leavetype: "Leave Type", holiday: "Holiday", communicationsource: "Communication Source",
  relatedto: "Related To", prospectamount: "Prospect Amount", policy: "Policy",
  myattendance: "My Attendance", myevaluation: "My Evaluation",
  myteamleavesrequest: "My Team Leaves Request", weightages: "Weightages",
  hrattendance: "HR Attendance", hrleaverequest: "HR Leave Request",
  ipaddress: "IP Address", pastdaysettings: "Past Day Settings",
  designation: "Designation", companyshifts: "Company Shifts",
  sessionout: "Session Out", userevaluation: "User Evaluation",
  performanceperiod: "Performance Period", acctivitylogs: "Activity Logs",
  dashboard: "Dashboard", mywfh: "My WFH", myteamwfhrequest: "My Team WFH Request",
  hrwfhrequest: "HR WFH Request", adjustment: "Adjustment",
};

const formatModuleName = (key) =>
  MODULE_NAMES[key.toLowerCase()] || key.charAt(0).toUpperCase() + key.slice(1);

const EditRole = () => {
  const companyId = DataParser.CompanyId();
  const [readPermssion, setReadPermissions] = useState([]);
  const [writePermissions, setWritePermissions] = useState([]);
  const [editPermissions, setEditPermissions] = useState([]);
  const [deletePermissions, setDeletePermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [id, setId] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const Roles = location?.state?.data;

  useEffect(() => {
    setIsLoading(true);
    const roleId = Roles?.id;
    CompanyService.showCompanyRole(roleId, companyId).then((res) => {
      const response = res.data;
      setName(response?.role[0]?.name);
      setId(response?.role[0]?.id);
      setPermissions(response.rolePermissions);
      setReadPermissions(response.user_permissions.Read);
      setWritePermissions(response.user_permissions.Write);
      setEditPermissions(response.user_permissions.Edit);
      setDeletePermissions(response.user_permissions.Delete);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    SocketService.connect();
    const adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo")) || {};
    const roleId = adminInfo?.user?.userdata?.role;
    if (roleId) SocketService.emit("registerRole", roleId);

    const handleUpdatePermissions = (data) => {
      const adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo")) || {};
      if (adminInfo?.user?.userdata?.role === data.roleId) {
        adminInfo.user.userdata.user_permissions = data.permissions;
        secureLocalStorage.setItem("adminInfo", JSON.stringify(adminInfo));
        window.dispatchEvent(new Event("permissionsUpdated"));
        message.info("Permissions updated in this session!");
      }
    };

    SocketService.on("updatePermissions", handleUpdatePermissions);
    return () => { SocketService.off("updatePermissions", handleUpdatePermissions); };
  }, []);

  const typeMap = {
    read: readPermssion,
    write: writePermissions,
    edit: editPermissions,
    delete: deletePermissions,
  };

  const modulePermissions = useMemo(() => {
    const map = {};
    const process = (list, type, suffix) => {
      list.forEach((p) => {
        const key = p.name.replace(new RegExp(suffix + "$"), "").toLowerCase();
        if (!map[key]) map[key] = { key, read: null, write: null, edit: null, delete: null };
        map[key][type] = p.id;
      });
    };
    process(readPermssion, "read", "-list");
    process(writePermissions, "write", "-create");
    process(editPermissions, "edit", "-edit");
    process(deletePermissions, "delete", "-delete");
    return Object.values(map).sort((a, b) =>
      formatModuleName(a.key).localeCompare(formatModuleName(b.key))
    );
  }, [readPermssion, writePermissions, editPermissions, deletePermissions]);

  const onPermissionsSelect = (permId) => {
    setPermissions((prev) =>
      prev.includes(permId) ? prev.filter((el) => el !== permId) : [...prev, permId]
    );
  };

  const isAllByType = (type) => {
    const list = typeMap[type];
    return list.length > 0 && list.every((p) => permissions.includes(p.id));
  };

  const toggleAllByType = (type) => {
    const ids = typeMap[type].map((p) => p.id);
    const allSelected = ids.every((id) => permissions.includes(id));
    if (allSelected) {
      setPermissions((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setPermissions((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const allPermissions = [...readPermssion, ...writePermissions, ...editPermissions, ...deletePermissions];
  const allSelected = allPermissions.length > 0 && allPermissions.every((p) => permissions.includes(p.id));

  const toggleAll = () => {
    const allIds = allPermissions.map((p) => p.id);
    if (allSelected) {
      setPermissions([]);
    } else {
      setPermissions(allIds);
    }
  };

  const onSubmitHandler = (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Role name is required!"); return; }
    setIsSending(true);
    try {
      CompanyService.updateCompanyRole(id, { name, permission: permissions, company_id: companyId })
        .then((res) => {
          if (res.data === "Un-Athunticated request") {
            message.error("Session expired! Please log in again.");
            secureLocalStorage.removeItem("adminInfo");
            secureLocalStorage.removeItem("admins");
            navigate("/");
            return;
          }
          if (res.Good === true) {
            message.success("Role updated successfully!");
            const adminInfo = JSON.parse(secureLocalStorage.getItem("adminInfo")) || {};
            if (adminInfo?.user?.userdata?.role === id) {
              const updatedPermissions = res?.userPermissions || adminInfo.user.userdata.user_permissions;
              adminInfo.user.userdata.user_permissions = updatedPermissions;
              secureLocalStorage.setItem("adminInfo", JSON.stringify(adminInfo));
              window.dispatchEvent(new Event("permissionsUpdated"));
              SocketService.emit("forceUpdatePermissions", { roleId: id, permissions: updatedPermissions });
            }
            navigate(`/${DataParser.CompanyIdentifierRole()}/roles`);
          } else {
            message.warning("No changes detected or something went wrong.");
          }
          setIsSending(false);
        })
        .catch((err) => {
          console.error("Update error:", err);
          message.error(err.response?.data?.message || "Failed to update role. Please try again!");
          setIsSending(false);
        });
    } catch (err) {
      console.error("Unexpected error:", err);
      message.error("Something went wrong! Please try again later.");
      setIsSending(false);
    }
  };

  const COLS = ["read", "write", "edit", "delete"];

  return (
    <>
      <Helmet>
        <title>Edit Role - Planit</title>
      </Helmet>

      <div className="main-content">
        <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
          <div className="container-fluid">

            <div className="row">
              <div className="col-12 px-0">
                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                  <div className="card-body p-0 pt-3">
                    <form onSubmit={onSubmitHandler}>

                      <div className="row mb-4 align-items-center">
                        <div className="col-md-6">
                          <div className="float-input-wrap">
                            <input
                              type="text"
                              className={error ? "is-invalid" : ""}
                              id="roleName"
                              placeholder=" "
                              value={name}
                              onChange={(e) => {
                                setName(e.target.value);
                                if (e.target.value.trim() !== "") setError("");
                              }}
                            />
                            <label htmlFor="roleName">Role Name*</label>
                          </div>
                          {error && <small className="text-danger mt-1 d-block">{error}</small>}
                        </div>
                        <div className="col-md-6 d-flex justify-content-end">
                          <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <Link to={`/${DataParser.CompanyIdentifierRole()}/employee-attendance`}>Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to={`/${DataParser.CompanyIdentifierRole()}/roles`}>Roles</Link>
                            </li>
                            <li className="breadcrumb-item active">Edit</li>
                          </ol>
                        </div>
                      </div>

                      <div className="row mb-4">
                        <div className="col-12">
                          <div className="table-responsive listing-table-wrapper">
                            <table className="table mb-0 custom-table listing-table">
                              <thead>
                                <tr className="tableHeaderbg">
                                  <th style={{ width: "36%", textAlign: "left" }}>
                                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                                      <Tooltip title="Select / deselect all permissions">
                                        <input
                                          type="checkbox"
                                          className="form-check-input mt-0"
                                          style={{ cursor: "pointer", flexShrink: 0 }}
                                          checked={allSelected}
                                          onChange={toggleAll}
                                        />
                                      </Tooltip>
                                      Select All
                                    </div>
                                  </th>
                                  {[
                                    { col: "read",   tip: "Permission to view and list records" },
                                    { col: "write",  tip: "Permission to create new records" },
                                    { col: "edit",   tip: "Permission to modify existing records" },
                                    { col: "delete", tip: "Permission to permanently remove records" },
                                  ].map(({ col, tip }) => (
                                    <th key={col} className="text-center">
                                      <div className="d-flex align-items-center justify-content-center" style={{ gap: "8px" }}>
                                        <input
                                          type="checkbox"
                                          className="form-check-input mt-0"
                                          style={{ cursor: "pointer", flexShrink: 0 }}
                                          checked={isAllByType(col)}
                                          onChange={() => toggleAllByType(col)}
                                        />
                                        {col.charAt(0).toUpperCase() + col.slice(1)}
                                        <Tooltip title={tip}>
                                          <i className="fa fa-info-circle" style={{ fontSize: "10px", opacity: 0.7, cursor: "help" }} />
                                        </Tooltip>
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {isLoading ? (
                                  <TableSkeleton cols={5} rows={6} />
                                ) : (
                                  modulePermissions.map((mod) => (
                                    <tr key={mod.key} className="text-center">
                                      <td style={{ textAlign: "left", fontWeight: 500, fontSize: "13px" }}>
                                        {formatModuleName(mod.key)}
                                      </td>
                                      {COLS.map((col) => (
                                        <td key={col}>
                                          {mod[col] !== null ? (
                                            <input
                                              type="checkbox"
                                              className="form-check-input mt-0"
                                              style={{ cursor: "pointer" }}
                                              checked={permissions.includes(mod[col])}
                                              onChange={() => onPermissionsSelect(mod[col])}
                                            />
                                          ) : (
                                            <span style={{ color: "#ccc", fontSize: "13px" }}>—</span>
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 d-flex justify-content-end">
                          <button
                            type="submit"
                            className="btn btn-primary waves-effect waves-light"
                            style={{ borderRadius: "5px", fontSize: "13px" }}
                            disabled={isSending || isLoading}
                          >
                            {isSending ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>

                    </form>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default EditRole;
